//#region Feedback Ratings

export interface IMessageFeedback {
  rating?: FeedbackRating;
  isRatingPending?: boolean;
}

/**
 * Enum for feedback rating values.
 * Using string values for better debugging and serialization.
 */
export enum FeedbackRating {
  ThumbsUp = 1,
  ThumbsDown = -1,
  Cleared = 0,
}

//#endregion

//#region Feedback Payloads

/**
 * Payload for updating feedback rating for a message.
 * @property messageId - The ID of the message being rated.
 * @property rating - The feedback rating value.
 */
export type MessageFeedbackPayload = {
  messageId: string;
  rating: FeedbackRating;
};

/**
 * Payload for setting or updating feedback text for a message.
 * @property messageId - The ID of the message.
 * @property text - The feedback text (optional for clearing).
 */
export type FeedbackTextPayload = {
  messageId: string;
  text?: string;
};

//#endregion

//#region Feedback Cooldowns

/**
 * Represents the payload for setting a cooldown with expiry for a message feedback.
 * @property messageId - The ID of the message.
 * @property expiry - Expiry timestamp in milliseconds since epoch.
 */
export type FeedbackCooldownPayload = {
  messageId: string;
  expiry: number;
};

/**
 * Represents a payload for clearing/removing a cooldown for a message feedback.
 */
export type ClearFeedbackCooldownPayload = Pick<FeedbackCooldownPayload, "messageId">;

//#endregion

//#region Feedback Text Entries

/**
 * Represents the state of a feedback text entry for a message from a user.
 * @property text - The feedback text content.
 * @property submittedAt - Timestamp when feedback was submitted (ms since epoch).
 */
export interface IFeedbackTextEntry {
  text: string;
  submittedAt?: number;
}

/**
 * Represents the state of a feedback cooldown entry for a message.
 * @property expiry - The timestamp when the cooldown expires (ms since epoch).
 * @property timeoutId - The ID of the timeout used to clear the cooldown.
 */
export interface IFeedbackCooldownEntry {
  expiry: number;
  timeoutId?: ReturnType<typeof setTimeout> | null;
}

//#endregion

//#region Feedback Service

/**
 * Parameters for saving feedback for a message.
 */
export type SaveFeedbackParams = {
  ExtBotId: number;
  ExtMsgId: string;
  Key: (typeof FeedbackKeys)[keyof typeof FeedbackKeys];
  Value?: string;
  ConvId: string;
  CompanyId: number;
};

/**
 * Response for saving feedback for a message.
 */
export interface SaveFeedbackResponse {
  status: boolean;
  statusMessage?: string;
}

/**
 * Constants for feedback error names to avoid repetition.
 */
export const FeedbackErrorNames = {
  SendRatingValidationError: "SEND_RATING_VALIDATION_ERROR",
  SendFeedbackValidationError: "SEND_FEEDBACK_VALIDATION_ERROR",
  SendMessageRatingError: "SEND_MESSAGE_RATING_ERROR",
  SendMessageFeedbackError: "SEND_MESSAGE_FEEDBACK_ERROR",
  FeedbackResponseError: "FEEDBACK_RESPONSE_ERROR",
} as const;

/**
 * Constants for feedback keys used in requests.
 */
export const FeedbackKeys = {
  Rating: "rating",
  Feedback: "feedback",
} as const;

/**
 * Type-safe feedback error names derived from constants.
 */
export type FeedbackErrorName = (typeof FeedbackErrorNames)[keyof typeof FeedbackErrorNames];

/**
 * Error response for saving feedback for a message.
 */
export interface SaveFeedbackError {
  name: FeedbackErrorName;
  message: string;
  cause?: any;
}

//#endregion
